import React, { useState } from "react";
import "./agent.css";
import AgentDetailModal from "../../components/AgentDetailModal/AgentDetailModal";
import CreateAgentModal from "../../components/CreateAgentModal/CreateAgentModal";
import EditAgentModal from "../../components/EditAgentModal/EditAgentModal";
import ConfirmActionModal from "../../components/ConfirmActionModal/ConfirmActionModal";
import { useToast } from "../../components/Toast/ToastContainer";
import { useNavigate } from "react-router-dom"; // Import nếu cần dùng navigate
import { useRolePermission } from "../../hooks/useRolePermission";
import { useLocalSearch } from "../../hooks/useLocalSearch";
const AgentList = () => {
    // 1. Khởi tạo data là mảng rỗng theo yêu cầu
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Thêm state loading

    const [search, setSearch] = useState({
        cifAgentParent: "",
        cifDltt: ""
    });

    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    // const [quickSearch, setQuickSearch] = useState("");
    const { searchTerm, setSearchTerm, filteredData } = useLocalSearch(data);
    const [isCreateAgentOpen, setIsCreateAgentOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [openActionModal, setOpenActionModal] = useState(false);
    const [actionType, setActionType] = useState("");
    const [targetAgent, setTargetAgent] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const pageSize = 10; // API pagination nếu cần, hiện tại dùng client-side pagination hoặc hiển thị hết

    const { role, permissions } = useRolePermission();

    // Helper: Map trạng thái từ API (English) sang UI (Tiếng Việt)
    const mapStatusToVN = (status) => {
        switch (status) {
            case "ACTIVE": return "Hoạt động";
            case "CLOSED": return "Đóng";
            case "LOCKED": return "Tạm khóa"; // Giả định status LOCKED
            default: return status;
        }
    };

    // Helper: Format tiền tệ
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return "";
        return Number(amount).toLocaleString('vi-VN') + " VND"; // Thêm đuôi VND nếu muốn
    };

    // 2. Hàm gọi API Search
    const handleSearch = async () => {
        // Kiểm tra nếu không nhập gì thì cảnh báo (hoặc cho phép tìm hết tùy logic)
        // Nếu API bắt buộc ít nhất 1 trường, có thể check ở đây.
        if (!search.cifAgentParent && !search.cifDltt) {
            toast.error("Vui lòng nhập thông tin tìm kiếm");
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem("access_token");

            // Xây dựng payload, chỉ gửi các trường có dữ liệu
            const payload = {};
            if (search.cifAgentParent) payload.parent_cif = search.cifAgentParent;
            if (search.cifDltt) payload.agent_cif = search.cifDltt;

            // Lưu ý: Curl mẫu có status: "ACTIVE", nhưng UI chưa có input chọn status.
            // Nếu bạn muốn tìm tất cả trạng thái, không gửi field này hoặc gửi null tùy API.
            // Nếu API bắt buộc status, bạn cần thêm Dropdown vào search box.
            // Tạm thời tôi để API tự quyết định nếu thiếu status.

            const response = await fetch("https://api.bvb.cybertech.com.vn/api/v1/admin/agents/search", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                // Xử lý lỗi 401 (Unauthorized) nếu token hết hạn
                if (response.status === 401) {
                    toast.error("Phiên đăng nhập hết hạn.");
                    navigate("/login");
                    return;
                }
                throw new Error("Lỗi khi gọi API tìm kiếm");
            }

            const result = await response.json();

            // 3. Mapping dữ liệu API trả về vào cấu trúc State của UI
            // API Response field -> UI State field
            const mappedData = (result.data || []).map(item => ({
                // --- 1. NHÓM DỮ LIỆU HIỂN THỊ TRÊN BẢNG (Giữ nguyên) ---
                id: item.id,
                cifAgentParent: item.parent_cif,
                idDltt: item.agent_code,
                cifDltt: item.cif_number,
                tenDltt: item.agent_name,
                soGcn: item.registration_number,
                trangThai: mapStatusToVN(item.status),
                tkGiaoDich: item.account_number,
                soLuongNhanVien: item.total_users,
                hanMucNgay: formatCurrency(item.daily_limit),
                hanMucThang: formatCurrency(item.monthly_limit),

                // Ghép chuỗi để hiển thị đẹp trên bảng
                cbnvQuanLy: `${item.manager_code || ''} - ${item.manager_name || ''}`,
                branchQuanLy: `${item.branch_code || ''} - ${item.business_unit_name || ''}`,

                thoiGianTao: item.created_at ? item.created_at.replace("T", " ") : "",

                // --- 2. NHÓM DỮ LIỆU ẨN (CẦN THIẾT CHO MODAL EDIT) ---
                // Bạn cần bổ sung các dòng dưới đây để Edit Modal có dữ liệu:

                // a. Thông tin người đại diện (Bạn đang thiếu cái này)
                cifNguoiDaiDien: item.representative_cif, // <--- CÁI BẠN CẦN: Map từ representative_cif
                tenNguoiDaiDien: item.representative_name,
                sdtNguoiDaiDien: item.representative_phone,

                // b. Thông tin chi tiết đại lý
                tenAgentChuQuan: "Đại lý cấp 1 (Mock)", // API chưa trả về tên, tạm giữ Mock hoặc tìm field khác
                taiKhoanGiaoDich: item.account_number,  // Dùng để fill vào dropdown tài khoản
                diaChiChiTiet: item.address,            // Map từ address
                isCenter: item.is_central,              // Map checkbox Đại lý trung tâm

                // c. Thông tin quản lý (để fill vào các ô input mã/tên rời)
                maCbnvQuanLy: item.manager_code,
                tenCbnvQuanLy: item.manager_name,
                maDvkdPhatTrien: item.business_unit_code,
                tenDvkdPhatTrien: item.business_unit_name,

                // d. Thông tin log hệ thống
                nguoiTao: item.created_by,
                nguoiChinhSuaGanNhat: item.updated_by,
                thoiGianChinhSuaGanNhat: item.updated_at ? item.updated_at.replace("T", " ") : "",
                ngayHieuLucHanMuc: "", // API chưa có, để trống

                agentPhone: item.representative_phone,

                khuVuc: item.region || "",
            }));

            setData(mappedData);
            console.log(mappedData);
            if (mappedData.length === 0) {
                toast.info("Không tìm thấy kết quả nào.");
            }

        } catch (error) {
            console.error(error);
            toast.error("Có lỗi xảy ra khi tìm kiếm.");
        } finally {
            setIsLoading(false);
        }
    };

    const statusClass = (status) => {
        switch (status) {
            case "Hoạt động":
                return "status-active";
            case "Đóng":
                return "status-closed";
            case "Tạm khóa":
                return "status-warning";
            default:
                return "";
        }
    };
    // --- MỚI: Hàm xử lý gọi API đổi trạng thái ---
    const handleConfirmStatusChange = async (reason) => {
        if (!targetAgent || !actionType) return;

        // Map action từ UI (lowercase) sang API (uppercase)
        const apiActionMap = {
            lock: "LOCK",
            unlock: "UNLOCK",
            close: "CLOSE"
        };
        const apiAction = apiActionMap[actionType];

        if (!apiAction) {
            toast.error("Hành động không hợp lệ");
            return;
        }

        try {
            const token = localStorage.getItem("access_token");

            // API: POST /api/v1/admin/agents/{id}/status?action={ACTION}
            // Mặc dù CURL không có body, nhưng ConfirmModal yêu cầu nhập lý do.
            // Tôi sẽ gửi lý do vào body phòng trường hợp Backend cần ghi log.
            const url = `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/${targetAgent.id}/status?action=${apiAction}`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ reason: reason }) // Gửi kèm lý do
            });

            if (!response.ok) {
                if (response.status === 401) {
                    toast.error("Phiên đăng nhập hết hạn.");
                    navigate("/login");
                    return;
                }
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || "Lỗi khi cập nhật trạng thái");
            }

            toast.success(`Cập nhật trạng thái ${apiAction} thành công!`);
            setOpenActionModal(false);

            // Reload lại bảng để thấy trạng thái mới
            handleSearch();

        } catch (error) {
            console.error("Lỗi update status:", error);
            toast.error(error.message || "Có lỗi xảy ra.");
        }
    };
    // --- THÊM MỚI: Hàm xử lý khi Edit Modal lưu thành công ---
    const handleSaveAgent = (updatedForm) => {
        // updatedForm: Là cục data từ EditAgentModal trả ra (chứa các field lẻ đã sửa)

        // 1. TÍNH TOÁN LẠI CÁC FIELD HIỂN THỊ (giống hệt logic lúc map API)
        // Nếu bạn sửa tên người quản lý, ta phải ghép lại chuỗi "Mã - Tên" để hiển thị ra bảng
        const computedFields = {
            cbnvQuanLy: `${updatedForm.maCbnvQuanLy || ''} - ${updatedForm.tenCbnvQuanLy || ''}`,
            branchQuanLy: `${updatedForm.maDvkdPhatTrien || ''} - ${updatedForm.tenDvkdPhatTrien || ''}`,
            // Format lại ngày nếu cần (ví dụ form trả về Date object, bảng cần chuỗi)
            // thoiGianChinhSuaGanNhat: ... (nếu cần xử lý date)
        };

        // 2. TẠO RA OBJECT HOÀN CHỈNH ĐỂ CẬP NHẬT STATE
        // Merge: Data cũ của dòng đó + Data mới từ form + Data vừa tính toán lại
        const finalAgentData = {
            ...selectedAgent, // Giữ lại các field cũ không bị sửa (như id, ngày tạo...)
            ...updatedForm,   // Ghi đè các field đã sửa (tenNguoiDaiDien,...)
            ...computedFields // Ghi đè các field hiển thị (cbnvQuanLy,...)
        };

        console.log("Data chuẩn bị update vào State:", finalAgentData);

        // 3. CẬP NHẬT VIEW CHI TIẾT (AgentDetailModal)
        // setSelectedAgent quyết định cái gì hiển thị trong Modal View
        setSelectedAgent(finalAgentData);

        // 4. CẬP NHẬT BẢNG DANH SÁCH (Table)
        setData((prevData) =>
            prevData.map((item) =>
                item.id === finalAgentData.id ? finalAgentData : item
            )
        );

        // 5. Đóng Modal
        setIsEditOpen(false);
    };
    const openConfirmAction = (agent, type) => {
        setTargetAgent(agent);
        setActionType(type);
        setOpenActionModal(true);
    };

    const actionMessage = {
        lock: "Bạn có chắc chắn muốn khóa đại lý này?",
        unlock: "Bạn có chắc chắn muốn mở khóa đại lý này?",
        close: "Bạn có chắc chắn muốn đóng đại lý này?"
    };

    return (
        <div className="agent-container">

            <div className="agent-header">
                <h2 className="title">Quản lý Đại lý thanh toán</h2>
                {/* 2. Chỉ hiện nút Tạo mới nếu có quyền canCreate (BANK_CREATE) */}
                {permissions.canCreate && (
                    <button
                        className="btn-create-agent"
                        onClick={() => setIsCreateAgentOpen(true)}
                    >
                        Tạo mới
                    </button>
                )}
            </div>
            {/* Form tìm kiếm */}
            <div className="search-box">
                <h3>Tìm kiếm Đại lý</h3>

                <div className="search-row">
                    <div className="search-item">
                        <label>CIF Agent chủ quản</label>
                        <input
                            type="text"
                            placeholder="Nhập CIF Agent chủ quản"
                            value={search.cifAgentParent}
                            onChange={(e) =>
                                setSearch({ ...search, cifAgentParent: e.target.value })
                            }
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>

                    <div className="search-item">
                        <label>CIF Đại lý thanh toán</label>
                        <input
                            type="text"
                            placeholder="Nhập CIF DLTT"
                            value={search.cifDltt}
                            onChange={(e) =>
                                setSearch({ ...search, cifDltt: e.target.value })
                            }
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>

                    <button
                        className="btn-search"
                        onClick={handleSearch}
                        disabled={isLoading}
                    >
                        {isLoading ? "Đang tìm..." : "Tìm kiếm"}
                    </button>
                </div>
            </div>

            {/* Bảng kết quả */}
            <div className="table-box">
                <div className="table-header">
                    <h3 className="table-title">Kết quả tìm kiếm</h3>

                    <div className="quick-search-wrapper">
                        <img src="/icons/icons8-search-100.png" alt="" className="quick-search-icon" />
                        <input
                            className="quick-search"
                            type="text"
                            placeholder="Tìm kiếm nhanh trong bảng..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-wrapper">
                    <table className="agent-table">
                        <thead>
                            <tr>
                                <th>Thao tác</th>
                                <th>CIF Agent chủ quản</th>
                                <th>ID ĐLTT</th>
                                <th>CIF ĐLTT</th>
                                <th>Tên ĐLTT</th>
                                <th>Số GCNĐKDN</th>
                                <th>Trạng thái</th>
                                <th>TKTT giao dịch</th>
                                <th>Số lượng nhân viên</th>
                                <th>Hạn mức ngày</th>
                                <th>Hạn mức tháng</th>
                                <th>CBNV quản lý</th>
                                <th>Branch quản lý</th>
                                <th>Thời gian tạo</th>
                            </tr>
                        </thead>

                        <tbody>
                            {/* 4. LOGIC HIỂN THỊ DỮ LIỆU VỚI FILTERED DATA */}
                            {isLoading ? (
                                <tr><td colSpan="14" style={{ textAlign: "center", padding: "20px" }}>Đang tải dữ liệu...</td></tr>
                            ) : filteredData.length > 0 ? (
                                // Map từ filteredData (đã lọc)
                                filteredData.map((row) => {
                                    const isClosed = row.trangThai === "Đóng";
                                    const isTempLocked = row.trangThai === "Tạm khóa";

                                    return (
                                        <tr key={row.id}>
                                            <td className="actions">
                                                <button className="icon-btn" title="Xem chi tiết" onClick={() => { setSelectedAgent(row); setDetailOpen(true); }}>
                                                    <img src="/icons/icons8-eye-100.png" alt="Xem" />
                                                </button>
                                                {permissions.canEdit && !isClosed && !isTempLocked && (
                                                    <button className="icon-btn" title="Chỉnh sửa" onClick={() => { setSelectedAgent(row); setIsEditOpen(true); }}>
                                                        <img src="/icons/icons8-pencil-100.png" alt="Sửa" />
                                                    </button>
                                                )}
                                                {permissions.canEdit && !isClosed && (
                                                    <button className="icon-btn" title={isTempLocked ? "Mở khóa" : "Khóa"} onClick={() => openConfirmAction(row, isTempLocked ? "unlock" : "lock")}>
                                                        <img src={isTempLocked ? "/icons/icons8-unlock-100.png" : "/icons/icons8-lock-100.png"} alt={isTempLocked ? "Mở khóa" : "Khóa"} />
                                                    </button>
                                                )}
                                                {permissions.canEdit && !isClosed && (
                                                    <button className="icon-btn" title="Đóng" onClick={() => openConfirmAction(row, "close")}>
                                                        <img src="/icons/icons8-block-100.png" alt="Đóng" />
                                                    </button>
                                                )}
                                            </td>
                                            <td>{row.cifAgentParent}</td>
                                            <td>{row.idDltt}</td>
                                            <td>{row.cifDltt}</td>
                                            <td>{row.tenDltt}</td>
                                            <td>{row.soGcn}</td>
                                            <td><span className={`status ${statusClass(row.trangThai)}`}>{row.trangThai}</span></td>
                                            <td>{row.tkGiaoDich}</td>
                                            <td>{row.soLuongNhanVien}</td>
                                            <td>{row.hanMucNgay}</td>
                                            <td>{row.hanMucThang}</td>
                                            <td>{row.cbnvQuanLy}</td>
                                            <td>{row.branchQuanLy}</td>
                                            <td>{row.thoiGianTao}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                // Xử lý khi không có dữ liệu
                                <tr>
                                    <td colSpan="8" style={{ padding: "20px", color: "#666", textAlign: "center" }}>
                                        {data.length > 0 && searchTerm
                                            ? "Không tìm thấy kết quả phù hợp với từ khóa"
                                            : "Chưa có dữ liệu tìm kiếm"
                                        }
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {data.length > 0 && (
                    <div className="pagination">
                        <span>Số lượng bản ghi: {filteredData.length}/{data.length} (trên 10 bản
          ghi/trang)</span>
                        {/* Logic phân trang ở đây hiện là tĩnh (hardcode), cần xử lý logic page sau */}
                        <div className="pager">
                            <button disabled={page === 1} onClick={() => setPage(page - 1)}>Trước</button>
                            <button className="active">{page}</button>
                            <button disabled>Sau</button>
                        </div>
                    </div>
                )}
            </div>
            <AgentDetailModal
                key={selectedAgent?.id ? `detail-${selectedAgent.id}` : 'detail'}
                isOpen={detailOpen}
                onClose={() => setDetailOpen(false)}
                agent={selectedAgent}
            />
            <CreateAgentModal
                isOpen={isCreateAgentOpen}
                onClose={() => setIsCreateAgentOpen(false)}
                onSubmit={(data) => {
                    toast.success("Tạo đại lý mới thành công!");
                    setIsCreateAgentOpen(false);
                    handleSearch(); // Reload lại bảng sau khi tạo mới
                }}
            />
            <EditAgentModal
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                agent={selectedAgent}
                // --- SỬA: Gọi hàm xử lý cập nhật state chúng ta vừa viết ---
                onSave={handleSaveAgent}
            />
            <ConfirmActionModal
                isOpen={openActionModal}
                message={actionMessage[actionType]}
                requireReason={true}
                onCancel={() => setOpenActionModal(false)}
                // --- THAY ĐỔI Ở ĐÂY: Gọi hàm API thay vì chỉ log ---
                onConfirm={handleConfirmStatusChange}
            />

        </div>
    );
};

export default AgentList;